import type { ReactNode, RefObject } from 'react';
import type { Chart as ChartJS } from 'chart.js';

import { ChartHeaderActions } from '@/components/charts/actions';
import type { MetricChartKind, MetricChartSpec } from '@/components/charts/generic';
import { MetricChart, useMetricChart } from '@/components/charts/generic';
import ContentBlock, {
    type ContentBlockSize,
    type ContentBlockSpacing,
} from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { DateRangePresetId } from '@/components/molecules/DateRangePicker/DateRangePresets';

const DEFAULT_CHART_HEIGHT = 280;
const FULL_SCREEN_CHART_HEIGHT = 520;

export interface OverviewChartCardProps<TKind extends MetricChartKind> {
    /** Already translated. */
    title: string;
    /** Already translated. Secondary line under the title. */
    subtitle?: string;
    /** Stable slug used as the downloaded PNG's filename, e.g. `'cage-growth'`. */
    chartName: string;
    spec: MetricChartSpec<TKind>;
    isLoading?: boolean;
    error?: unknown;
    /** Offers Try again in the error state. */
    onRetry?: () => void;
    isRetrying?: boolean;
    chartHeight?: number;
    dateFrom?: string;
    dateTo?: string;
    onDateChange?: (dates: DateRange) => void;
    defaultPresetId?: DateRangePresetId;
    contentBlockSize?: ContentBlockSize;
    contentBlockSpacing?: ContentBlockSpacing;
    /**
     * Defaults to `true`. Set `false` on pages that own a single page-level date
     * picker shared by every chart; the card then shows only its actions menu.
     */
    showDateRangePicker?: boolean;
    /** Defaults to `true`; see `ChartActionsMenuProps.csvExport`. */
    csvExport?: boolean;
    /** Extra content beside the legend — e.g. a right-axis selector. */
    legendRight?: ReactNode;
    titleIcon?: ReactNode;
    className?: string;
    /** Rendered inside the card, below the chart — e.g. a playback control. */
    footer?: ReactNode;
}

/**
 * Standard card for a cage overview chart: title, date picker and chart actions in
 * the header, the chart and its legend in the body.
 *
 * Lives under `routes/` rather than in the shared chart layer because it depends
 * on `ContentBlock`, a page-level layout component. Keeping that dependency here
 * is what lets `@/components/charts/generic` stay free of any import pointing
 * back into `routes/`.
 */
export default function OverviewChartCard<TKind extends MetricChartKind>(
    props: OverviewChartCardProps<TKind>
) {
    const {
        title,
        subtitle,
        chartName,
        spec,
        isLoading = false,
        error,
        onRetry,
        isRetrying,
        chartHeight,
        dateFrom,
        dateTo,
        onDateChange,
        defaultPresetId = 'none',
        showDateRangePicker = true,
        csvExport = true,
        contentBlockSize = 'default',
        contentBlockSpacing = 'none',
        legendRight,
        titleIcon,
        className,
        footer,
    } = props;

    const {
        data,
        options,
        legendItems,
        visibleSeries,
        toggleSeries,
        chartRef,
        isFullScreen,
        handleFullScreenToggle,
        fullScreenContainerRef,
    } = useMetricChart(spec);

    // `chartHeight` sets the in-page height; fullscreen always grows to at least
    // the fullscreen height, so a card with a modest inline height does not leave
    // most of the fullscreen viewport empty.
    const inPageHeight = chartHeight ?? DEFAULT_CHART_HEIGHT;
    const resolvedHeight = isFullScreen
        ? Math.max(inPageHeight, FULL_SCREEN_CHART_HEIGHT)
        : inPageHeight;

    return (
        <ContentBlock
            title={title}
            subtitle={subtitle}
            titleIcon={titleIcon}
            size={contentBlockSize}
            spacing={contentBlockSpacing}
            className={className}
            containerRef={fullScreenContainerRef}
            headerActions={
                <ChartHeaderActions
                    // Chart.js generics are invariant, so `Chart<TKind>` is not
                    // assignable to `Chart<'line' | 'bar'>`. The actions menu only reads
                    // the canvas, data and options and calls `resetZoom`, which exist on both.
                    chartRef={chartRef as RefObject<ChartJS<'line' | 'bar'>>}
                    chartName={chartName}
                    isFullScreen={isFullScreen}
                    onFullScreenToggle={handleFullScreenToggle}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateChange={onDateChange}
                    defaultPresetId={defaultPresetId}
                    showDateRangePicker={showDateRangePicker}
                    csvExport={csvExport}
                    disabled={isLoading}
                />
            }>
            <MetricChart
                kind={spec.kind}
                data={data}
                options={options}
                legendItems={legendItems}
                visibleSeries={visibleSeries}
                onToggleSeries={toggleSeries}
                chartRef={chartRef}
                height={resolvedHeight}
                isLoading={isLoading}
                error={error}
                onRetry={onRetry}
                isRetrying={isRetrying}
                legendRight={legendRight}
            />
            {footer}
        </ContentBlock>
    );
}
